if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', this.hasAttribute('data-cista-gallery') ? this.onSlideChanged.bind(this) : debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false, { scrollPage: !this.hasAttribute('data-cista-gallery') }));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        if (!event.detail.currentElement) return;
        if (this.hasAttribute('data-cista-gallery')) {
          if (this.cistaGalleryController || this.mql.matches || this.hasAttribute('data-cista-layout-changing')) return;
          const current = event.detail.currentElement;
          if (!current.classList.contains('is-active')) {
            this.activationVersion = (this.activationVersion || 0) + 1;
            this.elements.viewer.querySelectorAll('[data-media-id]').forEach(slide => slide.classList.toggle('is-active', slide === current));
            this.playActiveMedia(current);
          }
        }
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, options = {}) {
        if (this.cistaGalleryController && !this.mql.matches) {
          this.cistaGalleryController.activate(mediaId);
          return;
        }
        const scrollPage = options.scrollPage ?? !this.closest('cista-shop');
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        const activationVersion = this.activationVersion = (this.activationVersion || 0) + 1;
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        // Section responses reorder the main slides; retain existing thumbnail
        // buttons/listeners while keeping the CISTA gallery in the same order.
        if (this.hasAttribute('data-cista-pdp-scenes') && this.elements.thumbnails) {
          const thumbnailList = this.elements.thumbnails.querySelector('[id^="Slider-Thumbnails"]');
          this.elements.viewer.querySelectorAll('[data-media-id]').forEach((slide, index) => {
            const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${slide.dataset.mediaId}"]`);
            if (!thumbnail || !thumbnailList) return;
            thumbnailList.append(thumbnail);
            thumbnail.dataset.mediaPosition = String(index + 1);
            const button = thumbnail.querySelector('button');
            const label = button.getAttribute('aria-label');
            if (label) button.setAttribute('aria-label', label.replace(/\d+/, String(index + 1)));
          });
          this.elements.thumbnails.resetPages?.();
        }

        if (scrollPage) this.preventStickyHeader();
        window.setTimeout(() => {
          if (activationVersion !== this.activationVersion || !activeMedia.classList.contains('is-active')) return;
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (!scrollPage) return;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.hasAttribute('data-cista-gallery')) {
          this.dispatchEvent(new CustomEvent('cista:media-change', { detail: { mediaId: thumbnail.dataset.target } }));
          if (!this.mql.matches) return;
          const list = this.elements.thumbnails.querySelector('[id^="Slider-Thumbnails"]');
          if (!list) return;
          const top = thumbnail.offsetTop;
          const bottom = top + thumbnail.offsetHeight;
          if (top < list.scrollTop) list.scrollTo({ top, behavior: 'auto' });
          else if (bottom > list.scrollTop + list.clientHeight) list.scrollTo({ top: bottom - list.clientHeight, behavior: 'auto' });
          return;
        }
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        this.querySelectorAll('video').forEach(video => video.pause());
        this.querySelectorAll('product-model').forEach(model => model.modelViewerUI?.pause());
        this.querySelectorAll('.js-youtube').forEach(video => video.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*'));
        this.querySelectorAll('.js-vimeo').forEach(video => video.contentWindow?.postMessage('{"method":"pause"}', '*'));
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
